"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { deliveryPiiRetentionQueryKey } from "@/hooks/queries/delivery-retention"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import { useAuth } from "@/hooks/use-auth"
import type { DeliveryPiiRetentionPolicy } from "@/lib/deliveries/pii-retention-config"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  saveDeliveryPiiRetentionPolicyAction,
  type SaveDeliveryPiiRetentionPolicyInput,
} from "@/server-actions/delivery-retention"
import { useTranslations } from "next-intl"


type FormErrorSetter = (errors: Record<string, unknown>) => void

export function useSaveDeliveryPiiRetentionPolicyMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryRetention")

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const queryKey = deliveryPiiRetentionQueryKey(
    getQueryTenantId(user),
  )

  return useMutation({
    mutationFn: async (input: SaveDeliveryPiiRetentionPolicyInput & {
      setErrors?: FormErrorSetter
    }): Promise<DeliveryPiiRetentionPolicy> => {
      const { setErrors, ...actionInput } = input
      const result = await withQueryHandling(
        saveDeliveryPiiRetentionPolicyAction(actionInput),
        {
          loading: tUi("savingDeliveryPrivacyPolicy"),
          success: tUi("deliveryPrivacyPolicySaved"),
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: value => queryClient.setQueryData(queryKey, value),
  })
}
